<template>
	<van-popup v-model:show="visible" class="notice-detail-popup" teleport="body" :z-index="3000" :close-on-click-overlay="true">
		<section class="notice-detail" :aria-label="$t('notice.detail')" :aria-busy="loading">
			<button type="button" class="detail-close" :aria-label="$t('notice.close')" @click="visible = false"><van-icon name="cross" /></button>
			<h2>{{ title || $t('notice.message') }}</h2>
			<p v-if="time" class="detail-time">{{ time }}</p>
			<van-loading v-if="loading" color="#ffd44f" />
			<div v-else-if="error" class="detail-error" role="alert">
				<p>{{ $t('notice.loadFailed') }}</p>
				<button type="button" @click="$emit('retry')">{{ $t('notice.retry') }}</button>
			</div>
			<div v-else class="detail-content" tabindex="0" :aria-label="$t('notice.detail')" v-html="safeContent"></div>
			<button v-if="showAll" type="button" class="view-all" @click="$emit('view-all')">{{ $t('trading.all') }}</button>
		</section>
	</van-popup>
</template>

<script>
import { prepareNoticeContent } from '../utils/noticeContent';

export default {
	name: 'NoticeDetailPopup',
	props: {
		show: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
		error: { type: Boolean, default: false },
		title: { type: String, default: '' },
		time: { type: String, default: '' },
		content: { type: String, default: '' },
		showAll: { type: Boolean, default: false },
	},
	emits: ['update:show', 'retry', 'view-all'],
	computed: {
		visible: {
			get() { return this.show; },
			set(value) { this.$emit('update:show', value); },
		},
		safeContent() {
			return prepareNoticeContent(this.content).html;
		},
	},
};
</script>

<style lang="less" scoped>
.notice-detail-popup {
	width: calc(100% - 36px);
	max-width: 460px;
	max-height: 82vh;
	max-height: 82dvh;
	border: 1px solid #173453;
	border-radius: 22px;
	background: #0b213c;
	color: #fff;
	box-sizing: border-box;
	overflow: hidden;
}

.notice-detail {
	display: flex;
	flex-direction: column;
	min-height: 130px;
	max-height: 82vh;
	max-height: 82dvh;
	padding: 26px 20px;
	box-sizing: border-box;
}

.notice-detail :deep(.van-loading) {
	display: block;
	margin: 32px auto;
}

.detail-close {
	position: absolute;
	top: 8px;
	right: 8px;
	width: 40px;
	height: 40px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #ffd44f;
	cursor: pointer;
}

.detail-close :deep(.van-icon) { font-size: 14px; }

.notice-detail > h2 {
	flex-shrink: 0;
	margin: 0 30px 6px 0;
	font-size: 14px;
	line-height: 1.45;
	overflow-wrap: anywhere;
}

.detail-time {
	flex-shrink: 0;
	margin: 0;
	color: #9aacc5;
	font-size: 10px;
	line-height: 1.5;
}

.detail-error {
	display: grid;
	justify-items: center;
	gap: 14px;
	padding: 30px 0 12px;
	color: #a3b3cb;
	font-size: 14px;
	text-align: center;
}

.detail-error p { margin: 0; }

.detail-error button,
.view-all {
	min-height: 40px;
	padding: 9px 22px;
	border: 0;
	border-radius: 20px;
	background: #ffd44f;
	color: #01132b;
	font-size: 12px;
	font-weight: 700;
	cursor: pointer;
}

.view-all { flex-shrink: 0; margin-top: 18px; }

.notice-detail button:focus-visible,
.detail-content:focus-visible {
	outline: 2px solid #ffd44f;
	outline-offset: 2px;
}

.detail-content {
	min-height: 0;
	margin-top: 18px;
	padding-top: 16px;
	padding-inline-end: 8px;
	border-top: 1px solid #173453;
	overflow: auto;
	overscroll-behavior: contain;
	color: #fff;
	font-size: 12px;
	line-height: 1.65;
	word-break: break-word;
	white-space: pre-wrap;
	scrollbar-width: thin;
	scrollbar-color: #476181 #0b213c;
	scrollbar-gutter: stable;
}

.detail-content::-webkit-scrollbar,
.detail-content :deep(pre)::-webkit-scrollbar { width: 6px; height: 6px; }
.detail-content::-webkit-scrollbar-track,
.detail-content :deep(pre)::-webkit-scrollbar-track { background: #0b213c; border-radius: 8px; }
.detail-content::-webkit-scrollbar-thumb,
.detail-content :deep(pre)::-webkit-scrollbar-thumb { background: #476181; border-radius: 8px; }
.detail-content::-webkit-scrollbar-thumb:hover { background: #6b85a7; }
.detail-content::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
.detail-content::-webkit-scrollbar-corner { background: #0b213c; }

.detail-content :deep(img) { display: block; max-width: 100%; height: auto; border-radius: 8px; }
.detail-content :deep(a) { color: #ffd44f; text-decoration: underline; }
.detail-content :deep(table) { border-collapse: collapse; font-size: 12px; white-space: normal; }
.detail-content :deep(td),
.detail-content :deep(th) { padding: 8px; border: 1px solid #476181; }
.detail-content :deep(h1),
.detail-content :deep(h2),
.detail-content :deep(h3),
.detail-content :deep(h4),
.detail-content :deep(h5),
.detail-content :deep(h6) { margin: 12px 0 8px; font-size: 14px; }
.detail-content :deep(p) { margin: 0 0 12px; }
.detail-content :deep(ul),
.detail-content :deep(ol) { padding-inline-start: 22px; list-style-position: outside; }
.detail-content :deep(ul) { list-style-type: disc; }
.detail-content :deep(ol) { list-style-type: decimal; }
.detail-content :deep(blockquote) { margin: 12px 0; padding-inline-start: 12px; border-inline-start: 2px solid #ffd44f; color: #b8c7db; }
.detail-content :deep(pre) { max-width: 100%; overflow-x: auto; white-space: pre; scrollbar-width: thin; scrollbar-color: #476181 #0b213c; }

@media screen and (max-width: 374px) {
	.notice-detail { padding-right: 16px; padding-left: 16px; }
}
</style>
